import { useCallback, useState } from 'react';
import type { GQLLocale } from 'graphql-ts-types';
import { AuthenticationProvider, ThirdPartyProvider } from 'graphql-ts-types';

import { ThirdPartyProviderString } from 'common/enums/ThirdPartyProvider';
import { useGQLLocale } from 'common/hooks/locale';
import { dispatchTrackingEventGA4 } from 'common/tracking/dispatchTrackingEventGA4';
import { Event } from 'common/types/auth';
import { Element } from 'common/types/tracking';
import { captureException } from 'common/utils/error-reporter';
import { useLoadGoogleClient } from './load-google-client';
import type { PerformLogin } from './third-party-login';

type RefType = React.RefObject<HTMLDivElement | null>;
type CredentialResponse = google.accounts.id.CredentialResponse;

interface InitGoogleClientParams {
  callback: (response: CredentialResponse) => void;
}

interface RenderGoogleButtonParams {
  ref: React.RefObject<HTMLDivElement | null>;
  locale: GQLLocale;
  onClick: () => void;
}

interface UseGooglSignInParams {
  ref: RefType;
  setCommonError: () => void;
  performLogin: PerformLogin;
  placement?: string;
}

export const GOOGLE_CLIENT_ID =
  '297753693288-ju035lndnohh99q70u27663vjb81chm0.apps.googleusercontent.com';

const initGoogleClient = ({ callback }: InitGoogleClientParams) => {
  window.google.accounts.id.initialize({
    client_id: GOOGLE_CLIENT_ID,
    callback,
  });
};

const renderGoogleButton = ({ ref, locale, onClick }: RenderGoogleButtonParams) => {
  if (ref.current === null) {
    throw new Error('Google button ref is null');
  }

  const buttonWidth = ref.current.clientWidth.toString() + 'px';

  window.google.accounts.id.renderButton(ref.current, {
    type: 'standard',
    theme: 'outline',
    size: 'large',
    logo_alignment: 'center',
    width: buttonWidth,
    text: 'continue_with',
    locale,
    click_listener: onClick,
  });
};

export const useGoogleSignIn = ({
  ref,
  setCommonError,
  performLogin,
  placement,
}: UseGooglSignInParams) => {
  const locale = useGQLLocale();
  const [isInitialized, setIsInitialized] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const removeButton = (error: unknown) => {
    captureException(typeof error === 'string' ? new Error(error) : error);
    ref.current?.remove();
  };

  const onGoogleClientLoad = useCallback(() => {
    const trackButtonClick = () => {
      dispatchTrackingEventGA4({
        event: Event.LoginFormClick,
        element: Element.Button,
        button_text: ThirdPartyProviderString.google,
        provider: AuthenticationProvider.Google,
        ...(placement && { placement }),
      });
    };

    const initCallback = async (response: CredentialResponse) => {
      setIsLoading(true);

      if (response?.credential) {
        await performLogin(ThirdPartyProvider.Google, response.credential);
        setIsLoading(false);

        return;
      }

      setIsLoading(false);
      setCommonError();
      captureException(new Error('Google login failed with empty response'));
    };

    if (ref.current) {
      try {
        initGoogleClient({
          callback: initCallback,
        });

        renderGoogleButton({
          ref,
          locale,
          onClick: trackButtonClick,
        });

        setIsInitialized(true);
      } catch (error) {
        removeButton(error);
      }
    } else {
      captureException(new Error('Google button ref is null'));
    }
  }, [locale, performLogin, placement, setIsLoading]);

  useLoadGoogleClient({
    onLoad: onGoogleClientLoad,
    onError: removeButton,
  });

  return { isInitialized, isLoading };
};
